import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import {
  DynamicQuestionDefinition,
  DynamicQuestionAnswer,
  AIQuestionMappingResult
} from '../../types/lifeDomainQuestions';
import {
  DYNAMIC_QUESTION_REGISTRY,
  getDomainCanonicalQuestions,
  searchDynamicQuestions,
  evaluateDynamicQuestion,
  mapNaturalLanguageQuestion,
  getQuestionCoverageMatrix
} from '../../utils/v600/lifeDomainQuestionEngine';
import {
  Briefcase,
  Award,
  Coins,
  Heart,
  Crown,
  BookOpen,
  Home,
  Shield,
  Plane,
  Scale,
  Sparkles,
  Users,
  Compass,
  Calendar,
  Clock,
  Search,
  CheckCircle2,
  AlertTriangle,
  Target,
  Layers,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  Info,
  Sliders,
  Eye,
  FileText,
  Table,
  Check,
  Zap,
  Sparkle
} from 'lucide-react';

interface LifeDomainQuestionFrameworkViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  chartStyle?: ChartStyle;
  focusDate?: Date | string;
  onOpenConsultation?: (prompt?: string) => void;
}

interface DomainConfig {
  id: LifeThemeId;
  nameEn: string;
  nameHi: string;
  bhavaNote: string;
  icon: React.ComponentType<{ className?: string }>;
  defaultQuestionId: string;
}

const CANONICAL_DOMAINS: DomainConfig[] = [
  {
    id: 'career_job',
    nameEn: 'Career & Enterprise',
    nameHi: 'आजीविका एवं व्यवसाय',
    bhavaNote: 'Bhava 10 & D-10',
    icon: Briefcase,
    defaultQuestionId: 'q_career_current_state'
  },
  {
    id: 'business',
    nameEn: 'Commercial Venture & Startups',
    nameHi: 'व्यापार, स्टार्टअप व वाणिज्य',
    bhavaNote: 'Bhava 7, 10 & D-10',
    icon: Award,
    defaultQuestionId: 'q_business_feasibility'
  },
  {
    id: 'wealth_finance',
    nameEn: 'Wealth & Liquidity',
    nameHi: 'धन, कोष एवं लाभ',
    bhavaNote: 'Bhava 2 & 11',
    icon: Coins,
    defaultQuestionId: 'q_wealth_current_finances'
  },
  {
    id: 'marriage_relationship',
    nameEn: 'Marriage & Partnership',
    nameHi: 'विवाह एवं दांपत्य',
    bhavaNote: 'Bhava 7 & D-9',
    icon: Heart,
    defaultQuestionId: 'q_marriage_timing'
  },
  {
    id: 'children',
    nameEn: 'Children & Legacy',
    nameHi: 'संतान, कुल एवं वंश',
    bhavaNote: 'Bhava 5 & D-7',
    icon: Crown,
    defaultQuestionId: 'q_children_themes'
  },
  {
    id: 'education',
    nameEn: 'Higher Education & Exams',
    nameHi: 'उच्च विद्या एवं प्रतियोगी परीक्षा',
    bhavaNote: 'Bhava 4, 5 & D-24',
    icon: BookOpen,
    defaultQuestionId: 'q_education_current_phase'
  },
  {
    id: 'house_property',
    nameEn: 'Property & Inheritance',
    nameHi: 'भूमि, भवन व अचल संपत्ति',
    bhavaNote: 'Bhava 4, 8 & D-4',
    icon: Home,
    defaultQuestionId: 'q_property_current_matters'
  },
  {
    id: 'vehicle',
    nameEn: 'Vehicles & Conveyances',
    nameHi: 'वाहन, सुख व यात्रा साधन',
    bhavaNote: 'Bhava 4 & Venus',
    icon: Compass,
    defaultQuestionId: 'q_vehicle_buy_timing'
  },
  {
    id: 'health_vitality',
    nameEn: 'Health & Vitality',
    nameHi: 'आरोग्य, ऊर्जा व दीर्घायु',
    bhavaNote: 'Bhava 1, 6 & 8',
    icon: Shield,
    defaultQuestionId: 'q_health_vitality_active_themes'
  },
  {
    id: 'foreign_travel',
    nameEn: 'Foreign PR & Travel',
    nameHi: 'विदेश गमन, पीआर व प्रवास',
    bhavaNote: 'Bhava 9 & 12',
    icon: Plane,
    defaultQuestionId: 'q_foreign_current_phase'
  },
  {
    id: 'litigation_competition',
    nameEn: 'Protection & Court Cases',
    nameHi: 'विवाद शमन, न्यायालय व रक्षा',
    bhavaNote: 'Bhava 6 & Mars',
    icon: Scale,
    defaultQuestionId: 'q_legal_active_themes'
  },
  {
    id: 'spirituality',
    nameEn: 'Spirituality & Inner Path',
    nameHi: 'अध्यात्म व आंतरिक साधना',
    bhavaNote: 'Bhava 9 & 12',
    icon: Sparkles,
    defaultQuestionId: 'q_spirituality_active_themes'
  },
  {
    id: 'family_parents',
    nameEn: 'Siblings & Family Bond',
    nameHi: 'सहोदर, कुल व पारिवारिक सौहार्द',
    bhavaNote: 'Bhava 3 & 2',
    icon: Users,
    defaultQuestionId: 'q_family_active_themes'
  }
];

export const LifeDomainQuestionFrameworkView: React.FC<LifeDomainQuestionFrameworkViewProps> = ({
  kundali,
  lang = 'en',
  chartStyle = 'north',
  focusDate,
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';

  const [selectedDomainId, setSelectedDomainId] = useState<LifeThemeId>('marriage_relationship');
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>('q_marriage_timing');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<'seeker' | 'acharya'>('seeker');
  const [showSpecialized, setShowSpecialized] = useState<boolean>(false);
  const [showCoverageAudit, setShowCoverageAudit] = useState<boolean>(false);

  const selectedDomain = useMemo(() => {
    return CANONICAL_DOMAINS.find(d => d.id === selectedDomainId) || CANONICAL_DOMAINS[0];
  }, [selectedDomainId]);

  // Questions for active domain
  const { primaryQuestions, specializedQuestions } = useMemo(() => {
    return getDomainCanonicalQuestions(selectedDomainId);
  }, [selectedDomainId]);

  // AI Question Mapping & Search
  const searchResult = useMemo(() => {
    if (!searchQuery.trim()) {
      return { directMatches: primaryQuestions };
    }
    return searchDynamicQuestions(searchQuery, selectedDomainId);
  }, [searchQuery, selectedDomainId, primaryQuestions]);

  const visibleQuestions = searchResult.directMatches;
  const aiMappingResult: AIQuestionMappingResult | undefined = searchResult.aiMappingResult;

  // Evaluated Answer for selected question
  const evaluation: DynamicQuestionAnswer = useMemo(() => {
    return evaluateDynamicQuestion(selectedQuestionId, kundali, lang, { focusDate });
  }, [selectedQuestionId, kundali, lang, focusDate]);

  const activeQuestionDef = useMemo(() => {
    return (
      DYNAMIC_QUESTION_REGISTRY.find(q => q.id === selectedQuestionId) ||
      DYNAMIC_QUESTION_REGISTRY[0]
    );
  }, [selectedQuestionId]);

  const coverageMatrix = useMemo(() => {
    return getQuestionCoverageMatrix();
  }, []);

  const handleDomainSelect = (domain: DomainConfig) => {
    setSelectedDomainId(domain.id);
    setSelectedQuestionId(domain.defaultQuestionId);
    setSearchQuery('');
    setShowSpecialized(false);
  };

  const handleCustomQuerySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const mapped = mapNaturalLanguageQuestion(searchQuery, selectedDomainId);
    if (mapped.matchedQuestionId) {
      setSelectedQuestionId(mapped.matchedQuestionId);
      if (mapped.domainId !== selectedDomainId) {
        setSelectedDomainId(mapped.domainId);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* =================================================================== */}
      {/* 1. DOMAIN SELECTOR BAR                                              */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-3 shadow-xs space-y-3">
        <div className="flex items-center justify-between px-1 flex-wrap gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-astro-accent" />
            {isHi ? '१३ शास्त्रीय जीवन क्षेत्र चयन' : 'Select Classical Life Domain (13 Themes)'}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCoverageAudit(!showCoverageAudit)}
              className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-astro-secondary-surface text-astro-secondary-text hover:text-astro-text border border-astro-border flex items-center gap-1 cursor-pointer transition-all"
            >
              <Table className="w-3 h-3 text-astro-accent" />
              {isHi
                ? showCoverageAudit
                  ? 'ऑडिट तालिका छुपाएं'
                  : 'कवरेज ऑडिट देखें'
                : showCoverageAudit
                ? 'Hide Coverage Audit'
                : 'View Coverage Matrix'}
            </button>
            <span className="text-[11px] text-astro-muted hidden sm:inline">
              {isHi
                ? '६-१० विशिष्ट प्रामाणिक प्रश्न प्रति क्षेत्र'
                : '6–10 Curated Canonical Questions per Domain'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2">
          {CANONICAL_DOMAINS.map(domain => {
            const isSelected = selectedDomainId === domain.id;
            const Icon = domain.icon;
            const domainQCount = DYNAMIC_QUESTION_REGISTRY.filter(
              q => q.domainId === domain.id
            ).length;
            return (
              <button
                key={domain.id}
                onClick={() => handleDomainSelect(domain)}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all cursor-pointer relative ${
                  isSelected
                    ? 'bg-astro-primary border-astro-primary text-white shadow-xs scale-[1.02]'
                    : 'bg-white border-astro-border text-astro-secondary-text hover:text-astro-text hover:bg-astro-secondary-surface'
                }`}
              >
                <Icon
                  className={`w-4 h-4 mb-1 ${
                    isSelected ? 'text-astro-accent' : 'text-astro-muted'
                  }`}
                />
                <span className="text-xs font-bold leading-tight line-clamp-1">
                  {isHi ? domain.nameHi : domain.nameEn}
                </span>
                <div className="flex items-center gap-1 mt-0.5">
                  <span
                    className={`text-[10px] font-mono ${
                      isSelected ? 'text-white/80' : 'text-astro-muted'
                    }`}
                  >
                    {domain.bhavaNote}
                  </span>
                  <span
                    className={`text-[9px] font-mono px-1 rounded-full ${
                      isSelected
                        ? 'bg-white/20 text-white'
                        : 'bg-astro-secondary-surface text-astro-secondary-text'
                    }`}
                  >
                    {domainQCount}q
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Coverage Matrix Modal / Accordion */}
      {showCoverageAudit && (
        <div className="bg-white rounded-2xl border border-astro-border p-4 shadow-sm space-y-3 animate-in fade-in duration-200">
          <div className="flex items-center justify-between border-b border-astro-border pb-2">
            <div className="flex items-center gap-2">
              <Table className="w-4 h-4 text-astro-accent" />
              <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary">
                {isHi
                  ? 'प्रामाणिक प्रश्न कवरेज एवं कोई दोहराव नहीं ऑडिट तालिका'
                  : 'Canonical Question Coverage & Non-Repetition Audit Matrix'}
              </h4>
            </div>
            <span className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              {DYNAMIC_QUESTION_REGISTRY.length} Curated Questions · 0 Repetitions
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead>
                <tr className="bg-astro-secondary-surface text-astro-secondary-text uppercase text-[10px] font-bold">
                  <th className="p-2">Life Domain</th>
                  <th className="p-2">Canonical Questions</th>
                  <th className="p-2">Specialized</th>
                  <th className="p-2">Analytical Intents</th>
                  <th className="p-2">Duplicates</th>
                  <th className="p-2">Common Engine Mapped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border">
                {coverageMatrix.map(row => {
                  const dom = CANONICAL_DOMAINS.find(d => d.id === row.domainId);
                  return (
                    <tr key={row.domainId} className="hover:bg-astro-surface/50">
                      <td className="p-2 font-bold text-astro-text">
                        {isHi ? dom?.nameHi : dom?.nameEn}
                      </td>
                      <td className="p-2 font-mono font-bold text-astro-primary">
                        {row.primaryQuestionsCount}
                      </td>
                      <td className="p-2 font-mono text-astro-muted">
                        {row.specializedQuestionsCount}
                      </td>
                      <td className="p-2 font-mono">{row.categoriesCount}</td>
                      <td className="p-2 text-emerald-700 font-bold">
                        <span className="inline-flex items-center gap-1">
                          <Check className="w-3 h-3 text-emerald-600" /> 0 (Clean)
                        </span>
                      </td>
                      <td className="p-2 text-emerald-700 font-bold">
                        <span className="inline-flex items-center gap-1">
                          <Check className="w-3 h-3 text-emerald-600" /> 100% Core
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* 2. CURATED CANONICAL QUESTIONS (6-10 CLEAN LIST) & NLP SEARCH        */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-astro-border/70 pb-3">
          <div className="space-y-0.5">
            <span className="text-[10px] font-bold font-mono uppercase tracking-wider text-astro-muted">
              {isHi ? selectedDomain.nameHi : selectedDomain.nameEn}
            </span>
            <h3 className="text-sm md:text-base font-bold text-astro-primary">
              {isHi ? 'आप क्या समझना चाहते हैं?' : 'What would you like to understand?'}
            </h3>
          </div>

          {/* Ask Your Own Question (NLP Search Bar) */}
          <form
            onSubmit={handleCustomQuerySubmit}
            className="relative min-w-[280px] sm:min-w-[340px]"
          >
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-astro-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim()) {
                  const mapped = mapNaturalLanguageQuestion(
                    e.target.value,
                    selectedDomainId
                  );
                  if (mapped.matchedQuestionId && mapped.confidence !== 'LOW') {
                    setSelectedQuestionId(mapped.matchedQuestionId);
                  }
                }
              }}
              placeholder={
                isHi
                  ? 'अपना प्रश्न लिखें (उदा. शादी कब होगी?)...'
                  : 'Ask your own question (or search in English / Hinglish)...'
              }
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-astro-border bg-white text-xs text-astro-text placeholder:text-astro-muted focus:outline-none focus:ring-1.5 focus:ring-astro-primary shadow-2xs"
            />
          </form>
        </div>

        {/* AI Intent Mapping Notification Banner (When typing custom query) */}
        {searchQuery.trim() && aiMappingResult && (
          <div className="p-3 rounded-xl bg-astro-secondary-surface border border-astro-border flex items-center justify-between flex-wrap gap-2 text-xs animate-in fade-in duration-200">
            <div className="flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span className="text-astro-secondary-text">
                {isHi ? 'AI प्रश्न समझ (Intent Match):' : 'AI Question Understanding:'}
              </span>
              <span className="font-bold text-astro-text font-mono">
                {aiMappingResult.canonicalIntent}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800">
                {aiMappingResult.confidence} Confidence ({aiMappingResult.confidenceScore}%)
              </span>
              <span className="text-[10px] font-mono text-astro-muted">
                Language: {aiMappingResult.detectedLanguage}
              </span>
            </div>
          </div>
        )}

        {/* Clean Curated Question Buttons (Single clean list of 6-10 questions) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {visibleQuestions.length === 0 ? (
            <div className="p-6 text-center text-xs text-astro-secondary-text w-full bg-white rounded-xl border border-astro-border col-span-2">
              {isHi
                ? 'कोई मेल खाता प्रश्न नहीं मिला। कृपया अन्य शब्दों में लिखें।'
                : 'No matching canonical question found. Try rephrasing your question.'}
            </div>
          ) : (
            visibleQuestions.map(q => {
              const isSelected = selectedQuestionId === q.id;
              return (
                <button
                  key={q.id}
                  onClick={() => setSelectedQuestionId(q.id)}
                  className={`p-3 rounded-xl text-xs font-medium transition-all text-left border cursor-pointer flex items-center justify-between gap-2 ${
                    isSelected
                      ? 'bg-astro-sage-tint border-astro-primary text-astro-primary font-bold shadow-xs ring-1 ring-astro-primary/30 scale-[1.01]'
                      : 'bg-white border-astro-border text-astro-secondary-text hover:text-astro-text hover:bg-astro-secondary-surface'
                  }`}
                >
                  <div className="space-y-0.5 flex-1 pr-2">
                    <p className="font-semibold text-xs leading-snug">
                      {isHi ? q.questionHi : q.questionEn}
                    </p>
                    <span className="text-[10px] font-mono text-astro-muted block">
                      Intent: {q.canonicalIntent}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {q.intent === 'TIMING' && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[9px] font-mono font-bold">
                        {isHi ? 'काल' : 'Timing'}
                      </span>
                    )}
                    {q.popularChip && (
                      <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[9px] font-bold">
                        ★
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Specialized Legal / Severance Accordion (kept accessible, not dominating) */}
        {specializedQuestions.length > 0 && !searchQuery && (
          <div className="pt-2 border-t border-astro-border/50">
            <button
              onClick={() => setShowSpecialized(!showSpecialized)}
              className="text-xs text-astro-muted hover:text-astro-text font-medium flex items-center gap-1.5 cursor-pointer py-1"
            >
              {showSpecialized ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
              <span>
                {isHi
                  ? 'विशिष्ट विधिक एवं विच्छेद प्रश्न (Specialized Inquiries)'
                  : 'Specialized & Legal Inquiries (Severance, Alimony & Custody)'}
              </span>
            </button>

            {showSpecialized && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 animate-in fade-in duration-150">
                {specializedQuestions.map(q => {
                  const isSelected = selectedQuestionId === q.id;
                  return (
                    <button
                      key={q.id}
                      onClick={() => setSelectedQuestionId(q.id)}
                      className={`p-3 rounded-xl text-xs font-medium transition-all text-left border cursor-pointer flex items-center justify-between gap-2 ${
                        isSelected
                          ? 'bg-astro-sage-tint border-astro-primary text-astro-primary font-bold shadow-xs'
                          : 'bg-white/80 border-astro-border text-astro-secondary-text hover:text-astro-text hover:bg-astro-secondary-surface'
                      }`}
                    >
                      <div>
                        <p className="font-semibold text-xs leading-snug">
                          {isHi ? q.questionHi : q.questionEn}
                        </p>
                        <span className="text-[10px] font-mono text-astro-muted block">
                          Intent: {q.canonicalIntent}
                        </span>
                      </div>
                      <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-800 border border-rose-200 text-[9px] font-mono font-bold">
                        Legal
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* =================================================================== */}
      {/* 3. EVALUATED ANSWER PRESENTATION (SEEKER / ACHARYA)                */}
      {/* =================================================================== */}
      {evaluation && (
        <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs space-y-5">
          {/* Header Strip */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-astro-border/80 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-xs font-bold px-2.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                  {isHi ? 'प्रामाणिक निर्णय' : 'Astrological Verdict'}
                </span>
                <span className="text-xs px-2.5 py-0.5 rounded bg-astro-sage-tint text-astro-primary border border-astro-primary/20 font-medium">
                  {isHi ? 'प्रारब्ध लचीलापन:' : 'Karmic Elasticity:'}{' '}
                  {isHi
                    ? evaluation.karmicElasticityBadgeHi
                    : evaluation.karmicElasticityBadgeEn}
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-astro-text font-serif">
                {isHi ? activeQuestionDef.questionHi : activeQuestionDef.questionEn}
              </h3>
            </div>

            {/* Mode Selector & Net Score */}
            <div className="flex items-center gap-2 self-start md:self-auto">
              <div className="bg-astro-surface border border-astro-border rounded-xl p-1 flex items-center gap-1 text-xs">
                <button
                  onClick={() => setViewMode('seeker')}
                  className={`px-3 py-1 rounded-lg font-medium transition-all ${
                    viewMode === 'seeker'
                      ? 'bg-astro-primary text-white shadow-xs'
                      : 'text-astro-secondary-text hover:text-astro-text'
                  }`}
                >
                  {isHi ? 'जिज्ञासु' : 'Seeker'}
                </button>
                <button
                  onClick={() => setViewMode('acharya')}
                  className={`px-3 py-1 rounded-lg font-medium transition-all ${
                    viewMode === 'acharya'
                      ? 'bg-amber-600 text-white shadow-xs'
                      : 'text-astro-secondary-text hover:text-astro-text'
                  }`}
                >
                  {isHi ? 'आचार्य प्रमाण' : 'Acharya Deep Dive'}
                </button>
              </div>
            </div>
          </div>

          {/* Absolute Timing Window Banner (if question requires timing) */}
          {evaluation.timingWindow && (
            <div className="p-4 rounded-xl bg-amber-500/10 border-2 border-amber-500/30 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-amber-500/20 pb-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200">
                    {isHi
                      ? 'सटीक ज्योतिषीय कालखंड (Calculated Timing Window)'
                      : 'Calculated Timing Window'}
                  </span>
                </div>
                <span className="text-xs font-mono font-bold text-amber-900 bg-amber-500/20 px-2.5 py-0.5 rounded-full">
                  {isHi
                    ? evaluation.timingWindow.relativeTimingHi
                    : evaluation.timingWindow.relativeTimingEn}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 text-xs">
                <div>
                  <span className="text-[10px] text-astro-muted block uppercase font-bold">
                    {isHi ? 'प्रारंभ तिथि' : 'Start Date'}
                  </span>
                  <span className="font-bold text-astro-text font-mono">
                    {evaluation.timingWindow.startDate}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] text-astro-muted block uppercase font-bold">
                    {isHi ? 'चरम प्रभाव काल' : 'Calculated Peak'}
                  </span>
                  <span className="font-bold text-amber-800 dark:text-amber-300 font-mono">
                    {evaluation.timingWindow.peakDate || 'Peak Window Active'}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] text-astro-muted block uppercase font-bold">
                    {isHi ? 'समापन तिथि' : 'End Date'}
                  </span>
                  <span className="font-bold text-astro-text font-mono">
                    {evaluation.timingWindow.endDate}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Core Synthesis Verdict Card */}
          <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border shadow-2xs space-y-1.5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-astro-accent shrink-0" />
              <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                {isHi ? 'मुख्य शास्त्रीय परामर्श एवं निर्णय' : 'Executive Astrological Verdict'}
              </h4>
            </div>
            <p className="text-sm font-medium text-astro-text leading-relaxed">
              {isHi ? evaluation.verdictHi : evaluation.verdictEn}
            </p>
          </div>

          {/* Seeker vs Acharya Views */}
          {viewMode === 'seeker' ? (
            <div className="space-y-4">
              {/* Strategic Direction & Catalyst */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-white border border-astro-border space-y-1.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5 text-astro-accent" />
                    {isHi ? 'रणनीतिक दिशा (Strategic Guidance)' : 'Strategic Direction'}
                  </span>
                  <p className="text-xs text-astro-text leading-relaxed">
                    {isHi ? evaluation.strategicDirectionHi : evaluation.strategicDirectionEn}
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white border border-astro-border space-y-1.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    {isHi ? 'सावधानी एवं संभावित भूल (Trapdoors)' : 'Trapdoor & Vigilance'}
                  </span>
                  <p className="text-xs text-astro-text leading-relaxed">
                    {isHi ? evaluation.trapdoorHi : evaluation.trapdoorEn}
                  </p>
                </div>
              </div>

              {/* Surgical Upaya & Karmic Cost */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-1.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-astro-primary" />
                    {isHi ? 'शास्त्रीय उपाय (Vedic Upaya)' : 'Classical Vedic Upaya'}
                  </span>
                  <p className="text-xs text-astro-text leading-relaxed">
                    {isHi ? evaluation.surgicalUpayaHi : evaluation.surgicalUpayaEn}
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-1.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5 text-astro-muted" />
                    {isHi ? 'व्यय एवं प्रयास लागत (Vyaya Investment)' : 'Required Effort & Vyaya'}
                  </span>
                  <p className="text-xs text-astro-secondary-text leading-relaxed">
                    {isHi ? evaluation.vyayaCostHi : evaluation.vyayaCostEn}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Acharya Deep Dive Mode */
            <div className="space-y-4 animate-in fade-in duration-200">
              {/* Shloka Citation & Methodology */}
              <div className="p-4 rounded-xl bg-astro-surface border border-astro-border space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-600" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary">
                    {isHi
                      ? 'शास्त्रीय श्लोक एवं प्रमाण (Canonical Shloka Citation)'
                      : 'Classical Shloka Citation & Authority'}
                  </h4>
                </div>
                <div className="p-3 rounded-lg bg-amber-500/10 border-l-4 border-amber-600 text-xs font-serif italic text-astro-text">
                  {isHi
                    ? evaluation.acharyaForensic.shlokaCitationHi
                    : evaluation.acharyaForensic.shlokaCitationEn}
                </div>
                <div className="text-xs text-astro-secondary-text space-y-1">
                  <p className="font-semibold text-astro-text">
                    {isHi
                      ? 'पद्धति औचित्य (Methodology Justification):'
                      : 'Methodology Justification:'}
                  </p>
                  <p>
                    {isHi
                      ? evaluation.acharyaForensic.methodologyJustificationHi
                      : evaluation.acharyaForensic.methodologyJustificationEn}
                  </p>
                </div>
                <div className="text-xs text-astro-secondary-text space-y-1">
                  <p className="font-semibold text-astro-text">
                    {isHi
                      ? 'अन्य विधियों की आवश्यकता क्यों नहीं है (Completeness Justification):'
                      : 'Why Other Methods Not Required:'}
                  </p>
                  <p>
                    {isHi
                      ? evaluation.acharyaForensic.whyOtherMethodsNotRequiredHi
                      : evaluation.acharyaForensic.whyOtherMethodsNotRequiredEn}
                  </p>
                </div>
              </div>

              {/* Evidence Chain Matrix */}
              <div className="bg-white rounded-xl border border-astro-border p-4 space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                  <Sliders className="w-3.5 h-3.5 text-astro-accent" />
                  {isHi
                    ? 'साक्ष्य पदानुक्रम एवं भार (Evidence Chain Weights)'
                    : 'Evidence Chain & Factor Weights'}
                </h4>
                <div className="space-y-2">
                  {evaluation.evidenceChain.map((ev, idx) => (
                    <div
                      key={idx}
                      className="p-2.5 rounded-lg bg-astro-surface border border-astro-border/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                    >
                      <div>
                        <span className="text-[10px] font-mono text-astro-muted block">
                          {ev.hierarchyTier}
                        </span>
                        <span className="font-bold text-astro-text">{ev.factor}: </span>
                        <span className="text-astro-secondary-text">
                          {isHi ? ev.descriptionHi : ev.descriptionEn}
                        </span>
                      </div>
                      <span className="px-2 py-0.5 rounded bg-astro-primary text-white text-[10px] font-mono font-bold self-start sm:self-auto">
                        Weight: {ev.supportWeight}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Footer Disclaimer */}
          <div className="flex items-center gap-2 text-[11px] text-astro-muted pt-2 border-t border-astro-border/50">
            <Info className="w-3.5 h-3.5 text-astro-muted shrink-0" />
            <p>{isHi ? evaluation.disclaimerHi : evaluation.disclaimerEn}</p>
          </div>
        </div>
      )}
    </div>
  );
};
